import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiUrl } from "@/lib/api-client";

export type AuthMode = "none" | "email";

export type User = {
  email: string;
  name: string;
  picture?: string;
  role: "Admin" | "Manager" | "Felhasználó";
};

export type AuthLiteContextType = {
  user: User | null;
  authMode: AuthMode;
  isLoading: boolean;
  /** True when backend returned 403 not_approved (user exists but status is pending) */
  notApproved: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** Email mode: returns the loaded user, or null if /api/auth/me did not yield a session. */
  refreshUser: () => Promise<User | null>;
};

const AuthLiteCtx = createContext<AuthLiteContextType | null>(null);

function getAuthMode(): AuthMode {
  const v = import.meta.env.VITE_FEATURE_AUTH;
  return v === "email" ? "email" : "none";
}

const AUTH_MODE = getAuthMode();

function pickUser(data: { user?: { email?: string; name?: string; picture?: string; role?: string } | null }): User | null {
  const u = data?.user;
  if (!u?.email) return null;
  const role = u.role === "Admin" || u.role === "Manager" || u.role === "Felhasználó"
    ? u.role
    : "Felhasználó";
  return {
    email: u.email,
    name: u.name ?? u.email,
    picture: u.picture,
    role,
  };
}

function getStoredToken(): string | null {
  // Audit H-18: JWT must never live in localStorage/sessionStorage – any XSS
  // immediately exfiltrates it. Auth is now carried only by the httpOnly
  // `auth` cookie (set by /api/auth/login). We proactively clear any legacy
  // tokens left behind by older client versions.
  try {
    localStorage.removeItem("email_jwt");
    sessionStorage.removeItem("email_jwt");
  } catch {
    // noop – storage access denied (private mode)
  }
  return null;
}

export function AuthLiteProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notApproved, setNotApproved] = useState(false);

  const fetchMe = useCallback(async (): Promise<User | null> => {
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(apiUrl("/api/auth/me"), {
      credentials: "include",
      headers,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return pickUser(data);
  }, []);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    if (AUTH_MODE !== "email") {
      const demo: User = { email: "demo@local", name: "Demo User", role: "Admin" };
      setUser(demo);
      return demo;
    }
    const u = await fetchMe();
    setUser(u);
    return u;
  }, [fetchMe]);

  useEffect(() => {
    if (AUTH_MODE === "none" && user === null) {
      setUser({
        email: "demo@local",
        name: "Demo User",
        role: "Admin",
      });
    }
  }, [user]);

  useEffect(() => {
    const onNotApproved = () => setNotApproved(true);
    window.addEventListener("auth:not_approved", onNotApproved);
    return () => window.removeEventListener("auth:not_approved", onNotApproved);
  }, []);

  useEffect(() => {
    if (AUTH_MODE === "none") {
      setUser({
        email: "demo@local",
        name: "Demo User",
        role: "Admin",
      });
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const u = await fetchMe();
      if (!cancelled) setUser(u);
      if (!cancelled) setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  const login = useCallback(async () => {
    // Login is handled by the Login page; this is a no-op.
  }, []);

  const logout = useCallback(async () => {
    if (AUTH_MODE === "none") return;
    try {
      await fetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("email_jwt");
    sessionStorage.removeItem("email_jwt");
    setUser(null);
    setNotApproved(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      authMode: AUTH_MODE,
      isLoading,
      notApproved,
      login,
      logout,
      refreshUser,
    }),
    [user, isLoading, notApproved, login, logout, refreshUser],
  );

  return (
    <AuthLiteCtx.Provider value={value}>{children}</AuthLiteCtx.Provider>
  );
}

export function useAuthLite() {
  const ctx = useContext(AuthLiteCtx);
  if (!ctx) throw new Error("AuthLiteContext missing");
  return ctx;
}
